// File: src/components/InProgressCarousel.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/Inprogress_carousel.css";
import bigLeftArrow from "../images/bigLeftArrow.png";
import bigRightArrow from "../images/bigRightArrow.png";
import emptyStateIcon from "../images/emptyStateIcon.png";
import {
  fetchUserLibraryBooks,
  fetchPageDetails,
} from "../services/AllServices.js";
import { speakText } from "./utils/speechUtils.js";

const InProgressCarousel = () => {
  const [inProgressBooks, setInProgressBooks] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Calculate visibility states
  const maxIndex = Math.max(0, inProgressBooks.length - 7);
  const showArrows = inProgressBooks.length > 7; // Show arrows when more than 7 books
  const isBeginning = currentIdx === 0;
  const isEnd = currentIdx >= maxIndex;

  useEffect(() => {
    const fetchBooks = async () => {
      try {
        const response = await fetchUserLibraryBooks();
        if (response.status === "success") {
          const filteredBooks = response.data.filter(
            (book) => book.status === "inprogress" || book.status === "restarted"
          );
          setInProgressBooks(filteredBooks);
        }
      } catch (error) {
        console.error("Error fetching in-progress books:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBooks();
  }, []);

  const prevSlide = () => {
    setCurrentIdx((prevIdx) => Math.max(0, prevIdx - 1));
  };

  const nextSlide = () => {
    setCurrentIdx((prevIdx) => Math.min(maxIndex, prevIdx + 1));
  };

  if (loading) {
    return (
      <div className="carousel__wrap">
        <div className="carousel__skeleton-loader-lib">
          {[...Array(7)].map((_, index) => (
            <div key={index} className="carousel__skeleton-item"></div>
          ))}
        </div>
      </div>
    );
  }

  const handlePlayClick = async (event, bookID) => {
    event.preventDefault();
    if (!bookID) return;

    try {
      const response = await fetchPageDetails(bookID);
      if (response) {
        navigate("/audiobook-player", { state: { bookData: response } });
      }
    } catch (error) {
      console.error("Error in handlePlayClick:", error);
    }
  };

  return (
    <div className="carousel__wrap">
      {inProgressBooks.length === 0 ? (
        <div className="empty-state-container">
          <div
            className="genre_heading"
            tabIndex={0}
            onFocus={() =>
              speakText(
                `In Progress section. You have ${inProgressBooks.length} books in progress.`
              )
            }
          >
            In Progress
          </div>
          <div className="empty-state">
            <div className="emptyStateIconAndHeading">
              <div className="emptyStateIcon">
                <img src={emptyStateIcon} alt="No books" />
              </div>
              <div className="empty-state__heading">No Books in Progress</div>
            </div>
            <p className="empty-state__text">
              Start listening to your favorite books to see them here.
            </p>
            <a href="/" className="empty-state__cta">
              Browse Books
            </a>
          </div>
        </div>
      ) : (
        <div className="carousel__inner" role="region" aria-label="In Progress carousel">
          <button
            className={`carousel__btn ${isBeginning ? "disabled" : ""}`}
            onClick={prevSlide}
            disabled={isBeginning}
            tabIndex={isBeginning ? -1 : 0}
            onFocus={() => speakText("Previous books")}
            aria-label="Previous books"
            type="button"
          >
            <div className="carousel__btn--prev">
              {showArrows && (
                <img
                  src={bigLeftArrow}
                  alt="Previous"
                  className="carousel__btn-arrow"
                  style={{ visibility: showArrows ? "visible" : "hidden" }}
                />
              )}
            </div>
          </button>

          <div className="carousel__container">
            <div
              className="genre_heading"
              tabIndex={0}
              onFocus={() =>
                speakText(
                  `In Progress section. You have ${inProgressBooks.length} books in progress.`
                )
              }
            >
              In Progress
            </div>
            <div
              className="carousel__slide-list"
              style={{ transform: `translateX(-${currentIdx * (10.3 + 2)}vw)` }}
            >
              {inProgressBooks.map((book, index) => (
                <div key={index} className="carousel__slide-item">
                  {/* semantic button for each tile */}
                  <button
                    className="carousel__slide-button"
                    type="button"
                    onClick={(e) => handlePlayClick(e, book._id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handlePlayClick(e, book._id);
                      }
                    }}
                    onFocus={() => speakText(book.title)}
                    aria-label={`Play ${book.title}`}
                  >
                    <img src={book.thumbnail} alt={`${book.title}`} className="carousel__slide-img" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <button
            className={`carousel__btn ${isEnd ? "disabled" : ""}`}
            onClick={nextSlide}
            disabled={isEnd}
            tabIndex={isEnd ? -1 : 0}
            onFocus={() => speakText("Next books")}
            aria-label="Next books"
            type="button"
          >
            <div className="carousel__btn--next">
              {showArrows && (
                <img
                  src={bigRightArrow}
                  alt="Next"
                  className="carousel__btn-arrow"
                  style={{ visibility: showArrows ? "visible" : "hidden" }}
                />
              )}
            </div>
          </button>
        </div>
      )}
    </div>
  );
};

export default InProgressCarousel;
